import React, { memo, useCallback, useMemo } from "react";
import { FaPlus, FaMinus } from "react-icons/fa";
import { MenuItem } from "../redux/slices/menuSlice";
import { formatPrice, parsePrice } from "../utils/priceUtils";
import { VegDotIcon, DietaryOverlayIcon } from "./DietaryIcon";
import { getPrimaryDietaryType } from "../utils/dietaryUtils";
import { getCartQuantity, getCartItemId } from "../utils/cartUtils";

interface BasicMenuItemCardProps {
  item: MenuItem;
  onItemClick?: (item: MenuItem) => void;
  onAddToCart?: (item: MenuItem) => void;
  showAddToCart?: boolean;
  cartItems?: any[];
  onUpdateQuantity?: (cartItemId: string, quantity: number) => void;
  onIncrementQuantity?: (item: MenuItem) => void;
  onDecrementQuantity?: (itemId: string) => void;
}

// Subcomponent: Menu Item Image
const MenuItemImage: React.FC<{
  item: MenuItem;
  isOutOfStock: boolean;
  onImageError: (e: React.SyntheticEvent<HTMLImageElement>) => void;
}> = ({ item, isOutOfStock, onImageError }) => (
  <div className="relative aspect-[4/3] overflow-hidden bg-gray-50">
    {item.image ? (
      <img
        src={item.image}
        alt={`${item.name} - Delicious menu item available for order`}
        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        onError={onImageError}
        loading="lazy"
      />
    ) : (
      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
        <span
          className="text-4xl font-bold uppercase"
          style={{ color: '#FA2C37' }}
        >
          {item.name.charAt(0)}
        </span>
      </div>
    )}

    {/* Dietary Icons Overlay */}
    <div className="absolute top-3 right-3 z-10">
      <DietaryOverlayIcon item={item} size="md" />
    </div>
  </div>
);

// Subcomponent: Menu Item Info
const MenuItemInfo: React.FC<{ item: MenuItem }> = ({ item }) => (
  <div className="p-4 pb-3 flex-grow flex flex-col">
    {/* Title + Veg/Non-Veg */}
    <div className="flex items-start gap-2 mb-2">
      {getPrimaryDietaryType(item) !== 'non-vegetarian' && (
        <div className="flex-shrink-0 mt-1">
          <VegDotIcon item={item} size="md" />
        </div>
      )}
      <h3 className="text-lg font-semibold text-gray-900 line-clamp-1 leading-tight">
        {item.name}
      </h3>
    </div>

    {/* Description */}
    {item.description && (
      <p className="text-gray-600 text-sm leading-relaxed mb-3 line-clamp-2">
        {item.description}
      </p>
    )}

    {/* Price - Push to bottom */}
    <div className="mt-auto">
      <span className="text-xl font-bold text-gray-900">
        {formatPrice(item.price)}
      </span>
    </div>
  </div>
);

// Subcomponent: Add to Cart Button or Quantity Controls
const AddToCartButton: React.FC<{
  item: MenuItem;
  isOutOfStock: boolean;
  showAddToCart: boolean;
  cartQuantity: number;
  cartItemId: string;
  onAddToCart: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onUpdateQuantity?: (cartItemId: string, quantity: number) => void;
  onIncrementQuantity?: (item: MenuItem) => void;
  onDecrementQuantity?: (itemId: string) => void;
}> = ({ item, isOutOfStock, showAddToCart, cartQuantity, cartItemId, onAddToCart, onUpdateQuantity, onIncrementQuantity, onDecrementQuantity }) => {
  if (!showAddToCart) return null;

  const handleMinus = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    // Use smart handler if available, fallback to simple handler
    if (onDecrementQuantity) {
      onDecrementQuantity(item.pk_id.toString());
    } else if (onUpdateQuantity) {
      onUpdateQuantity(cartItemId, cartQuantity - 1);
    }
  }, [onDecrementQuantity, onUpdateQuantity, item.pk_id, cartItemId, cartQuantity]);

  const handlePlus = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (cartQuantity > 0) {
      // Item in cart - use smart increment if available
      if (onIncrementQuantity) {
        onIncrementQuantity(item);
      } else if (onUpdateQuantity) {
        onUpdateQuantity(cartItemId, cartQuantity + 1);
      }
    } else {
      // Not in cart - use add to cart
      onAddToCart(e as any);
    }
  }, [cartQuantity, onIncrementQuantity, onUpdateQuantity, item, cartItemId, onAddToCart]);

  // Show quantity controls if item is in cart
  if (cartQuantity > 0) {
    return (
      <div className="w-full py-3 px-4 flex items-center justify-center gap-3 bg-red-500 rounded-b-xl min-h-[48px]">
        <button
          onClick={handleMinus}
          className="p-1 hover:scale-110 focus:outline-none transition-transform"
          aria-label="Decrease quantity"
        >
          <FaMinus className="w-4 h-4 text-white" />
        </button>
        <span className="font-bold text-xl text-white min-w-[2rem] text-center">{cartQuantity}</span>
        <button
          onClick={handlePlus}
          className="p-1 hover:scale-110 focus:outline-none transition-transform"
          aria-label="Increase quantity"
        >
          <FaPlus className="w-4 h-4 text-white" />
        </button>
      </div>
    );
  }

  // Show Add button if not in cart
  return (
    <button
      type="button"
      disabled={isOutOfStock}
      className={`w-full py-3 px-4 text-base font-bold transition-all duration-200 rounded-b-xl min-h-[48px] focus:outline-none ${
        isOutOfStock
          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
          : 'bg-red-500 text-white hover:bg-red-600 active:bg-red-700 hover:shadow-md'
      }`}
      onClick={onAddToCart}
      aria-label={`Add ${item.name} to cart${isOutOfStock ? ' - Out of Stock' : ''}`}
      aria-disabled={isOutOfStock}
      data-testid={`add-to-cart-${item.id || item.pk_id}`}
    >
      {isOutOfStock ? 'Out of Stock' : 'Add'}
    </button>
  );
};

const BasicMenuItemCard: React.FC<BasicMenuItemCardProps> = ({
  item,
  onItemClick,
  onAddToCart,
  showAddToCart = true,
  cartItems = [],
  onUpdateQuantity,
  onIncrementQuantity,
  onDecrementQuantity,
}) => {
  // Early exit - only hide if disabled or not verified, but show out-of-stock items
  if (!item.is_enabled || !item.is_item_verified) {
    return null;
  }

  // Check if item is out of stock
  const isOutOfStock = !item.inventory_status;

  // Get cart quantity for this item
  const cartQuantity = useMemo(() => 
    getCartQuantity(item.pk_id.toString(), cartItems),
    [item.pk_id, cartItems]
  );

  const cartItemId = useMemo(() => 
    getCartItemId(item.pk_id.toString()),
    [item.pk_id]
  );

  const handleImageError = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      (e.target as HTMLImageElement).src = `https://via.placeholder.com/300x200/f8f9fa/6c757d?text=${encodeURIComponent(
        item.name
      )}`;
    },
    [item.name]
  );

  const handleCardClick = useCallback(() => {
    onItemClick?.(item);
  }, [onItemClick, item]);

  const handleAddToCart = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (!isOutOfStock) {
        onAddToCart?.(item);
      }
    },
    [onAddToCart, item, isOutOfStock]
  );

  return (
    <div
      className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer group flex flex-col"
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      aria-label={`View details for ${item.name}, priced at ${parsePrice(
        item.price
      )}${isOutOfStock ? ' - Out of Stock' : ''}`}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") handleCardClick();
      }}
      data-testid={`menu-item-card-${item.id || item.pk_id}`}
    >
      <MenuItemImage 
        item={item} 
        isOutOfStock={isOutOfStock} 
        onImageError={handleImageError} 
      />
      <MenuItemInfo item={item} />
      <AddToCartButton 
        item={item}
        isOutOfStock={isOutOfStock}
        showAddToCart={showAddToCart}
        cartQuantity={cartQuantity}
        cartItemId={cartItemId}
        onAddToCart={handleAddToCart}
        onUpdateQuantity={onUpdateQuantity}
        onIncrementQuantity={onIncrementQuantity}
        onDecrementQuantity={onDecrementQuantity}
      />
    </div>
  );
};

// Optimized memoization - only check critical props that affect rendering
const areEqual = (prev: BasicMenuItemCardProps, next: BasicMenuItemCardProps) => {
  return (
    // Core item identity and critical display props
    (prev.item.id || prev.item.pk_id) === (next.item.id || next.item.pk_id) &&
    prev.item.price === next.item.price &&
    prev.item.is_enabled === next.item.is_enabled &&
    prev.item.inventory_status === next.item.inventory_status &&
    prev.item.is_item_verified === next.item.is_item_verified &&
    // Component behavior props
    prev.showAddToCart === next.showAddToCart &&
    prev.onItemClick === next.onItemClick &&
    prev.onAddToCart === next.onAddToCart &&
    prev.cartItems === next.cartItems
  );
};

export default memo(BasicMenuItemCard, areEqual);
